import DashboardContainer from "../../../containers/admin";
import Header from "../../../components/admin/header";
import { useState, useEffect } from "react";
import api from "../../../utils/apiConfig";
import toast from "react-hot-toast";
import {
  Plus,
  Trash2,
  Trophy,
  Users,
  Calendar,
  Clock,
  Target,
  Search,
  Filter,
  Download,
  X,
  ChevronDown
} from "lucide-react";

const PredictWinAdmin = () => {
  const [games, setGames] = useState([]);
  const [winners, setWinners] = useState([]);
  const [winnersStats, setWinnersStats] = useState({
    totalWins: 0,
    totalUniqueWinners: 0,
    totalAmountWon: 0,
    averageWinAmount: 0
  });
  const [availableMatches, setAvailableMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [winnersSearch, setWinnersSearch] = useState("");
  const [matchSearch, setMatchSearch] = useState("");
  const [showMatchDropdown, setShowMatchDropdown] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const [gameForm, setGameForm] = useState({
    matchId: "",
    potentialWin: "",
    participationFee: "",
    maxParticipants: "",
    isActive: true
  });

  useEffect(() => {
    fetchGames();
    fetchWinners();
    fetchWinnersStats();
    fetchAllMatches();
  }, []);

  useEffect(() => {
    // Filter matches for the selected date (only upcoming/not played matches)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0);
    
    const filtered = allMatches.filter(match => {
      const matchDate = match.matchDate;
      const isSelectedDate = matchDate === selectedDate;
      
      // For today, only show matches that haven't been played
      // For future dates, show all matches
      if (selectedDateObj.getTime() === today.getTime()) {
        const isUpcoming = !match.played && new Date(match.date) >= today;
        return isSelectedDate && isUpcoming;
      } else if (selectedDateObj > today) {
        // Future date - show all matches for that date
        return isSelectedDate;
      }
      
      // Past dates should not be selectable, but handle edge case
      return false;
    });
    
    setAvailableMatches(filtered);
  }, [selectedDate, allMatches]);

  const fetchGames = async () => {
    try {
      const response = await api.get("/predict/with-status");
      const { predictGames } = response.data;

      console.log(predictGames)
      
      // Transform the API response to match the expected format
      const transformedGames = predictGames.map(game => ({
        id: game._id,
        fixtureId: game.fixture.fixtureId,
        homeTeam: game.fixture.homeTeam,
        awayTeam: game.fixture.awayTeam,
        homeLogo: game.fixture.homeTeamEmblem,
        awayLogo: game.fixture.awayTeamEmblem,
        league: game.fixture.league,
        leagueLogo: game.fixture.leagueEmblem,
        matchDate: new Date(game.fixture.date).toISOString().split('T')[0],
        matchTime: new Date(game.fixture.date).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }),
        potentialWin: game.potentialWin,
        participationFee: game.participationFee,
        maxParticipants: game.maxParticipants,
        currentParticipants: game.participants.length,
        isActive: game.status === "active",
        played: game.fixture.played,
        resultPrediction: game.fixture.resultPrediction,
        overUnderPrediction: game.fixture.overUnderPrediction,
        ggngPrediction: game.fixture.ggngPrediction,
        wonResultPrediction: game.fixture.wonResultPrediction,
        wonGGNGPrediction: game.fixture.wonGGNGPrediction,
        wonOverUnderPrediction: game.fixture.wonOverUnderPrediction,
        createdAt: game.createdAt,
        updatedAt: game.updatedAt
      }));
      
      setGames(transformedGames);
    } catch (error) {
      console.error("Error fetching games:", error);
      toast.error("Error fetching games. Please try again.");
      setGames([]);
    }
  };

  const fetchWinners = async () => {
    try {
      const response = await api.get("/predict/winners/yesterday");
      const { winners } = response.data;
      
      // Transform the API response to match the expected format
      const transformedWinners = winners.map(winner => ({
        id: winner.user._id,
        userName: winner.user.fullName,
        userEmail: winner.user.email,
        phoneNumber: winner.user.phoneNumber,
        walletBalance: winner.user.walletBalance,
        match: `${winner.predictGame.fixture.homeTeam} vs ${winner.predictGame.fixture.awayTeam}`,
        homeTeam: winner.predictGame.fixture.homeTeam,
        awayTeam: winner.predictGame.fixture.awayTeam,
        predictionHomeScore: winner.homePrediction,
        predictionAwayScore: winner.awayPrediction,
        actualHomeScore: winner.predictGame.fixture.homeScore,
        actualAwayScore: winner.predictGame.fixture.awayScore,
        winningAmount: winner.predictGame.potentialWin,
        participationFee: winner.predictGame.participationFee,
        wonAt: winner.wonAt,
        matchDate: winner.predictGame.fixture.date
      }));
      
      setWinners(transformedWinners);
    } catch (error) {
      console.error("Error fetching winners:", error);
      toast.error("Error fetching yesterday's winners. Please try again.");
      setWinners([]);
    }
  };

  const fetchWinnersStats = async () => {
    try {
      const response = await api.get("/predict/winners/stats");
      const { stats } = response.data;
      
      setWinnersStats({
        totalWins: stats.totalWins || 0,
        totalUniqueWinners: stats.totalUniqueWinners || 0,
        totalAmountWon: stats.totalAmountWon || 0,
        averageWinAmount: stats.averageWinAmount || 0
      });
    } catch (error) {
      console.error("Error fetching winners stats:", error);
      // Keep default values if API fails
    }
  };

  const fetchAllMatches = async () => {
    try {
      const response = await api.get("/fixtures");
      const { fixtures } = response.data;
      
      // Transform the API response to match the same structure
      const transformedMatches = fixtures.map(match => ({
        id: match._id,
        fixtureId: match.fixtureId,
        homeTeam: match.homeTeam,
        awayTeam: match.awayTeam,
        homeTeamEmblem: match.homeTeamEmblem || `https://api.dicebear.com/7.x/identicon/svg?seed=${match.homeTeam}`,
        awayTeamEmblem: match.awayTeamEmblem || `https://api.dicebear.com/7.x/identicon/svg?seed=${match.awayTeam}`,
        league: match.league,
        leagueEmblem: match.leagueEmblem || `https://api.dicebear.com/7.x/identicon/svg?seed=${match.league}`,
        date: match.date, // Keep original ISO format
        played: match.played,
        resultPrediction: match.resultPrediction,
        overUnderPrediction: match.overUnderPrediction,
        ggngPrediction: match.ggngPrediction,
        wonResultPrediction: match.wonResultPrediction,
        wonGGNGPrediction: match.wonGGNGPrediction,
        wonOverUnderPrediction: match.wonOverUnderPrediction,
        // Additional fields for admin display
        homeLogo: match.homeTeamEmblem || `https://api.dicebear.com/7.x/identicon/svg?seed=${match.homeTeam}`,
        awayLogo: match.awayTeamEmblem || `https://api.dicebear.com/7.x/identicon/svg?seed=${match.awayTeam}`,
        leagueLogo: match.leagueEmblem || `https://api.dicebear.com/7.x/identicon/svg?seed=${match.league}`,
        matchDate: new Date(match.date).toISOString().split('T')[0], // Convert to YYYY-MM-DD format for display
        matchTime: new Date(match.date).toLocaleTimeString('en-GB', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }), // Convert to HH:MM format for display
      }));
      
      setAllMatches(transformedMatches);
    } catch (error) {
      console.error("Error fetching matches:", error);
      // Fallback to empty array if API fails
      setAllMatches([]);
    }
  };

  const handleAddGame = () => {
    setGameForm({
      matchId: "",
      potentialWin: "",
      participationFee: "",
      maxParticipants: "",
      isActive: true
    });
    setMatchSearch("");
    // Reset to today's date when opening modal
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setShowGameModal(true);
  };


  const handleSubmitGame = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const selectedMatch = availableMatches.find(match => match.id === gameForm.matchId);
      
      if (!selectedMatch) {
        toast.error("Please select a valid match");
        setLoading(false);
        return;
      }

      // Validate potential win amount
      const potentialWin = parseInt(gameForm.potentialWin);
      if (!gameForm.potentialWin || gameForm.potentialWin === "" || isNaN(potentialWin) || potentialWin <= 0) {
        toast.error("Please enter a valid potential winning amount");
        setLoading(false);
        return;
      }

      // Validate participation fee
      const participationFee = parseInt(gameForm.participationFee);
      if (!gameForm.participationFee || gameForm.participationFee === "" || isNaN(participationFee) || participationFee < 0) {
        toast.error("Please enter a valid participation fee");
        setLoading(false);
        return;
      }

      // Validate max participants
      const maxParticipants = parseInt(gameForm.maxParticipants);
      if (!gameForm.maxParticipants || gameForm.maxParticipants === "" || isNaN(maxParticipants) || maxParticipants <= 0) {
        toast.error("Please enter a valid maximum number of participants");
        setLoading(false);
        return;
      }

      // Prepare game data for /predict/create endpoint
      const gameData = {
        fixtureId: selectedMatch.id, // MongoDB _id of the fixture
        potentialWin: potentialWin,
        participationFee: participationFee,
        maxParticipants: maxParticipants
      };

      // Create new predict game using /predict/create endpoint
      // This endpoint works for both today and future dates
      await api.post("/predict/create", gameData);
      
      // Show success message with match details
      const matchDate = new Date(selectedMatch.matchDate).toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'short', 
        day: 'numeric' 
      });
      
      setShowGameModal(false);
      fetchGames(); // Refresh the games list
      toast.success(`Game created successfully for ${selectedMatch.homeTeam} vs ${selectedMatch.awayTeam} on ${matchDate}!`);
    } catch (error) {
      console.error("Error saving game:", error);
      
      // Provide more specific error messages
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "Error creating game. Please try again.";
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    if (window.confirm("Are you sure you want to delete this game?")) {
      try {
        await api.delete(`/predict/${gameId}`);
        fetchGames();
        toast.success("Game deleted successfully!");
      } catch (error) {
        console.error("Error deleting game:", error);
        const errorMessage = error.response?.data?.message || "Error deleting game. Please try again.";
        toast.error(errorMessage);
      }
    }
  };

  // Filter games based on search and status
  const filteredGames = games.filter(game => {
    const matchesSearch = 
      game.homeTeam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.awayTeam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.league?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === "all" || 
      (statusFilter === "active" && game.isActive) ||
      (statusFilter === "inactive" && !game.isActive);
    
    return matchesSearch && matchesStatus;
  });

  // Filter winners based on search
  const filteredWinners = winners.filter(winner =>
    winner.userName?.toLowerCase().includes(winnersSearch.toLowerCase()) ||
    winner.userEmail?.toLowerCase().includes(winnersSearch.toLowerCase()) ||
    winner.match?.toLowerCase().includes(winnersSearch.toLowerCase()) ||
    winner.homeTeam?.toLowerCase().includes(winnersSearch.toLowerCase()) ||
    winner.awayTeam?.toLowerCase().includes(winnersSearch.toLowerCase())
  );

  // Filter matches for dropdown search
  const filteredMatches = availableMatches.filter(match =>
    match.homeTeam?.toLowerCase().includes(matchSearch.toLowerCase()) ||
    match.awayTeam?.toLowerCase().includes(matchSearch.toLowerCase()) ||
    match.league?.toLowerCase().includes(matchSearch.toLowerCase())
  );

  const getSelectedMatch = () => {
    return availableMatches.find(match => match.id === gameForm.matchId);
  };

  const handleExport = () => {
    const dataToExport = filteredGames.map(game => ({
      "Match": `${game.homeTeam} vs ${game.awayTeam}`,
      "League": game.league,
      "Date": new Date(game.matchDate).toLocaleDateString(),
      "Time": game.matchTime,
      "Potential Win": `₦${game.potentialWin?.toLocaleString()}`,
      "Participants": `${game.currentParticipants || 0}/${game.maxParticipants}`,
      "Status": game.isActive ? "Active" : "Inactive"
    }));

    const csvHeaders = Object.keys(dataToExport[0] || {}).join(',');
    const csvRows = dataToExport.map(row => 
      Object.values(row).map(value => `"${value}"`).join(',')
    ).join('\n');
    
    const csvContent = `${csvHeaders}\n${csvRows}`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `predict-win-games-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success("Data exported successfully!");
  };

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active Only" },
    { value: "inactive", label: "Inactive Only" }
  ];

  return (
    <DashboardContainer>
      <Header title="Predict & Win Management" />
      
      {/* Quick Stats - Mobile Responsive */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-3 md:p-4 border border-gray-100">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target className="text-blue-600 w-4 h-4 md:w-5 md:h-5" />
            </div>
            <div className="ml-3">
              <p className="text-xs md:text-sm font-medium text-gray-600">Active Games</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900">
                {games.filter(g => g.isActive).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-3 md:p-4 border border-gray-100">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <span className="text-green-600 font-bold text-sm">₦</span>
            </div>
            <div className="ml-3">
              <p className="text-xs md:text-sm font-medium text-gray-600">Total Winnings</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900">
                ₦{winnersStats.totalAmountWon.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-3 md:p-4 border border-gray-100">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Trophy className="text-purple-600 w-4 h-4 md:w-5 md:h-5" />
            </div>
            <div className="ml-3">
              <p className="text-xs md:text-sm font-medium text-gray-600">Total Wins</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900">{winnersStats.totalWins}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-3 md:p-4 border border-gray-100">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Users className="text-yellow-600 w-4 h-4 md:w-5 md:h-5" />
            </div>
            <div className="ml-3">
              <p className="text-xs md:text-sm font-medium text-gray-600">Unique Winners</p>
              <p className="text-lg md:text-2xl font-bold text-gray-900">{winnersStats.totalUniqueWinners}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Games Section */}
      <div className="bg-white rounded-lg shadow mb-6 border border-gray-100">
        <div className="p-4 md:p-6 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <h3 className="text-lg font-semibold">Manage Predict & Win Games</h3>
            <button
              onClick={handleAddGame}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 w-full lg:w-auto justify-center text-sm md:text-base"
            >
              <Plus className="w-4 h-4" />
              Add New Game
            </button>
          </div>
          
          {/* Search and Filter Bar - Mobile Responsive */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search games by team or league..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            
            <div className="flex gap-2">
              {/* Filter Dropdown */}
              <div className="relative flex-1">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 justify-between text-sm"
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden xs:block">
                    {statusOptions.find(opt => opt.value === statusFilter)?.label}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {showFilterDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setStatusFilter(option.value);
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                          statusFilter === option.value ? 'bg-blue-50 text-blue-600' : ''
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button 
                onClick={handleExport}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:block">Export</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-4 md:p-6">
          <div className="overflow-x-auto -mx-4 md:mx-0">
            {/* Mobile Card View */}
            <div className="block lg:hidden space-y-4 px-4 md:px-0">
              {filteredGames.map((game) => (
                <div key={game.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <img className="w-6 h-6 rounded-full" src={game.homeLogo} alt={game.homeTeam} />
                      <div className="text-center min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{game.homeTeam}</div>
                        <div className="text-xs text-gray-500">vs</div>
                        <div className="text-sm font-medium truncate">{game.awayTeam}</div>
                      </div>
                      <img className="w-6 h-6 rounded-full" src={game.awayLogo} alt={game.awayTeam} />
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      game.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {game.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <img className="w-4 h-4 rounded-full" src={game.leagueLogo} alt={game.league} />
                      <span className="text-xs">{game.league}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600 text-xs">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(game.matchDate).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {game.matchTime}
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="font-bold text-green-600 text-sm">
                        ₦{game.potentialWin?.toLocaleString()}
                      </div>
                      <div className="flex items-center gap-1 text-gray-600 text-xs">
                        <Users className="w-3 h-3" />
                        {game.currentParticipants || 0}/{game.maxParticipants}
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className="bg-blue-600 h-1.5 rounded-full" 
                        style={{ 
                          width: `${Math.min(100, ((game.currentParticipants || 0) / game.maxParticipants) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-center items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => handleDeleteGame(game.id)}
                      className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50"
                      title="Delete Game"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Match
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    League & Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Potential Win
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Participants
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredGames.map((game) => (
                  <tr key={game.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 rounded-lg">
                          <img className="h-6 w-6 rounded-full" src={game.homeLogo} alt={game.homeTeam} />
                        </div>
                        <div className="ml-3 text-center">
                          <div className="text-sm font-medium text-gray-900">{game.homeTeam}</div>
                          <div className="text-xs text-gray-500">vs</div>
                          <div className="text-sm font-medium text-gray-900">{game.awayTeam}</div>
                        </div>
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 rounded-lg ml-3">
                          <img className="h-6 w-6 rounded-full" src={game.awayLogo} alt={game.awayTeam} />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <img className="h-5 w-5 rounded-full mr-2" src={game.leagueLogo} alt={game.league} />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{game.league}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar size={10} />
                            {new Date(game.matchDate).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock size={10} />
                            {game.matchTime}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-bold text-green-600">
                        ₦{game.potentialWin?.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Users size={14} className="text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {game.currentParticipants || 0}/{game.maxParticipants}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full" 
                          style={{ 
                            width: `${Math.min(100, ((game.currentParticipants || 0) / game.maxParticipants) * 100)}%` 
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        game.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {game.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleDeleteGame(game.id)}
                          className="text-red-600 hover:text-red-900 p-1.5 rounded hover:bg-red-50"
                          title="Delete Game"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredGames.length === 0 && (
              <div className="text-center py-12">
                <Target className="mx-auto text-gray-400 mb-4 w-12 h-12" />
                <p className="text-gray-500 text-lg mb-2">No games found</p>
                <p className="text-gray-400 text-sm mb-4">Try adjusting your search or filter criteria</p>
                <button
                  onClick={handleAddGame}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto text-sm"
                >
                  <Plus size={16} />
                  Add New Game
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Winners Section */}
      <div className="bg-white rounded-lg shadow border border-gray-100">
        <div className="p-4 md:p-6 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="text-yellow-600 w-5 h-5" />
              <h3 className="text-lg font-semibold">Yesterday&apos;s Winners</h3>
            </div>
            
            {/* Winners Search */}
            <div className="flex-1 lg:max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search winners by name, match..."
                  value={winnersSearch}
                  onChange={(e) => setWinnersSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="p-4 md:p-6">
          <div className="overflow-x-auto -mx-4 md:mx-0">
            {/* Mobile Card View for Winners */}
            <div className="block lg:hidden space-y-4 px-4 md:px-0">
              {filteredWinners.map((winner) => (
                <div key={winner.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <Trophy className="text-white w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-900 text-sm truncate">{winner.userName}</div>
                      <div className="text-gray-500 text-xs truncate">{winner.userEmail}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="font-medium text-sm">{winner.match}</div>
                    <div className="text-gray-600 text-xs">{new Date(winner.matchDate).toLocaleDateString()}</div>
                    
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex-1">
                        <div className="text-xs text-gray-500">Prediction</div>
                        <div className="font-semibold bg-blue-50 px-2 py-1 rounded text-xs text-center">
                          {winner.predictionHomeScore} - {winner.predictionAwayScore}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="text-xs text-gray-500">Actual</div>
                        <div className="font-semibold bg-green-50 px-2 py-1 rounded text-xs text-center">
                          {winner.actualHomeScore} - {winner.actualAwayScore}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <div className="text-sm font-bold text-green-600 flex items-center gap-1">
                        <span className="text-xs">₦</span>
                        {winner.winningAmount?.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(winner.wonAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View for Winners */}
            <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Winner
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Match
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prediction
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actual Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Winnings
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Won At
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredWinners.map((winner) => (
                  <tr key={winner.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                          <Trophy className="text-white" size={12} />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {winner.userName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {winner.userEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">{winner.match}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <Calendar size={10} />
                        {new Date(winner.matchDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-semibold bg-blue-50 px-3 py-1 rounded text-xs">
                        {winner.predictionHomeScore} - {winner.predictionAwayScore}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      <span className="font-semibold bg-green-50 px-3 py-1 rounded text-xs">
                        {winner.actualHomeScore} - {winner.actualAwayScore}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-bold text-green-600 flex items-center gap-1">
                        <span className="text-xs">₦</span>
                        {winner.winningAmount?.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar size={10} />
                        {new Date(winner.wonAt).toLocaleDateString()}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={10} />
                        {new Date(winner.wonAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredWinners.length === 0 && (
              <div className="text-center py-12">
                <Trophy className="mx-auto text-gray-400 mb-4 w-12 h-12" />
                <p className="text-gray-500">No winners found</p>
                <p className="text-gray-400 text-sm mt-1">Try adjusting your search criteria</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Game Modal */}
      {showGameModal && (
        <div className="fixed inset-0 bg-black/30 overflow-y-auto h-full w-full z-50 flex items-start justify-center p-4">
          <div className="relative bg-white rounded-lg border border-gray-100 shadow-lg w-full max-w-2xl my-8">
            <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Target size={20} />
                Add New Predict & Win Game
              </h3>
              <button 
                onClick={() => setShowGameModal(false)} 
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmitGame} className="p-4 md:p-6 space-y-4 md:space-y-6">
              {/* Date Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date for Match *
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setGameForm({...gameForm, matchId: ""}); // Clear selected match when date changes
                    }}
                    min={new Date().toISOString().split('T')[0]} // Prevent selecting past dates
                    className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 md:py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select a date to view available matches for that day
                </p>
              </div>

              {/* Selected Match Preview */}
              {getSelectedMatch() && (
                <div className="bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-200">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2 md:mb-3">Selected Match</h4>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <img className="h-6 w-6 md:h-8 md:w-8 rounded-full" src={getSelectedMatch().homeLogo} alt={getSelectedMatch().homeTeam} />
                      <span className="font-medium text-sm">{getSelectedMatch().homeTeam}</span>
                    </div>
                    <span className="text-gray-500 font-medium text-sm">vs</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{getSelectedMatch().awayTeam}</span>
                      <img className="h-6 w-6 md:h-8 md:w-8 rounded-full" src={getSelectedMatch().awayLogo} alt={getSelectedMatch().awayTeam} />
                    </div>
                  </div>
                  <div className="mt-2 text-xs md:text-sm text-gray-600 flex flex-wrap gap-3 md:gap-4">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(getSelectedMatch().matchDate).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock size={12} />
                      {getSelectedMatch().matchTime}
                    </div>
                    <div className="flex items-center gap-1">
                      <Trophy size={12} />
                      {getSelectedMatch().league}
                    </div>
                  </div>
                </div>
              )}

              {/* Match Selection with Enhanced Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Match * {availableMatches.length > 0 && (
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      ({availableMatches.length} matches available for {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })})
                    </span>
                  )}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowMatchDropdown(!showMatchDropdown)}
                    className="w-full border border-gray-300 rounded-lg px-3 md:px-4 py-2 md:py-3 text-left flex items-center justify-between bg-white hover:border-gray-400 transition-colors text-sm"
                  >
                    <span className={gameForm.matchId ? "text-gray-900 font-medium" : "text-gray-500"}>
                      {getSelectedMatch() 
                        ? `${getSelectedMatch().homeTeam} vs ${getSelectedMatch().awayTeam}` 
                        : "Choose a match..."
                      }
                    </span>
                    <ChevronDown size={16} className="text-gray-400" />
                  </button>
                  
                  {showMatchDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-20 max-h-64 md:max-h-80 overflow-hidden">
                      {/* Enhanced Search Header */}
                      <div className="p-2 md:p-3 border-b border-gray-200 bg-gray-50">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 md:w-4 md:h-4" />
                          <input
                            type="text"
                            placeholder="Search matches by team or league..."
                            value={matchSearch}
                            onChange={(e) => setMatchSearch(e.target.value)}
                            className="w-full pl-8 md:pl-10 pr-3 md:pr-4 py-2 border border-gray-300 rounded text-xs md:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {filteredMatches.length} matches found
                        </div>
                      </div>
                      
                      {/* Matches List */}
                      <div className="max-h-48 md:max-h-60 overflow-y-auto">
                        {filteredMatches.map((match) => (
                          <button
                            key={match.id}
                            type="button"
                            onClick={() => {
                              setGameForm({...gameForm, matchId: match.id});
                              setShowMatchDropdown(false);
                              setMatchSearch("");
                            }}
                            className={`w-full px-3 md:px-4 py-2 md:py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors ${
                              gameForm.matchId === match.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 md:gap-3">
                              <div className="flex-shrink-0">
                                <img className="w-6 h-6 md:w-8 md:h-8 rounded-full" src={match.homeLogo} alt={match.homeTeam} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-xs md:text-sm text-gray-900 truncate">
                                  {match.homeTeam} vs {match.awayTeam}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                                  <span className="flex items-center gap-1">
                                    <Calendar size={10} />
                                    {new Date(match.matchDate).toLocaleDateString()}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock size={10} />
                                    {match.matchTime}
                                  </span>
                                  <span className="truncate">{match.league}</span>
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                <img className="w-6 h-6 md:w-8 md:h-8 rounded-full" src={match.awayLogo} alt={match.awayTeam} />
                              </div>
                            </div>
                          </button>
                        ))}
                        
                        {filteredMatches.length === 0 && (
                          <div className="px-4 py-6 md:py-8 text-center">
                            <Search className="mx-auto text-gray-300 mb-2 w-6 h-6 md:w-8 md:h-8" />
                            <p className="text-xs md:text-sm text-gray-500">
                              {matchSearch ? "No matches found for your search" : "No matches available for this date"}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              {matchSearch ? "Try different search terms" : "Try selecting a different date"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Potential Win (₦) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold text-sm">₦</span>
                    <input
                      type="number"
                      min="0"
                      required
                      value={gameForm.potentialWin}
                      onChange={(e) => setGameForm({...gameForm, potentialWin: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-2 md:py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter amount"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Participation Fee (₦) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold text-sm">₦</span>
                    <input
                      type="number"
                      min="0"
                      required
                      value={gameForm.participationFee}
                      onChange={(e) => setGameForm({...gameForm, participationFee: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg pl-8 pr-4 py-2 md:py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter fee"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Participants *
                  </label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      min="1"
                      required
                      value={gameForm.maxParticipants}
                      onChange={(e) => setGameForm({...gameForm, maxParticipants: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 md:py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter max participants"
                    />
                  </div>
                </div>
              </div>


              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 md:pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowGameModal(false)}
                  className="px-4 md:px-6 py-2 md:py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2 justify-center order-2 sm:order-1 transition-colors"
                >
                  <X size={16} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !gameForm.matchId}
                  className="px-4 md:px-6 py-2 md:py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2 justify-center order-1 sm:order-2 transition-colors"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus size={16} />
                      Add Game
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardContainer>
  );
};

export default PredictWinAdmin;
